import { useEffect, useState } from 'react';

import { ChildDocument } from './ChildDocument';
import Common from '../../stores/Common';
import { DocumentTypeEnum } from '../../utilities/DbConstants';
import ExpandableRow from '../../components/shared/ExpandableRow';
import { ILookupModel } from '../../interfaces/ILookup';
import { IViewDocumentModel } from '../../interfaces/IDocument';
import { Link } from 'react-router-dom';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { Stack } from 'react-bootstrap';
import { StrategyTypes } from '../../utilities/Constants';
import { ToggleIcon } from '../../shared/ToggleIcon';
import classNames from 'classnames';
import { useGetChildDocuments } from '../../shared/react-query-hooks/useGetMyChildDocuments';
import { useZoneContext } from '../../contexts/useZoneContext';

interface IDocumentRowProps {
    canDelete: boolean;
    document: IViewDocumentModel;
    documentsDownloading: string[];
    documentsWithErrors: string[];
    documentTypes: ILookupModel[];
    onDeleteClick: (document: IViewDocumentModel, onSuccess?: () => void) => Promise<void>;
    onDownloadClick: (event: React.FormEvent<any>, document: IViewDocumentModel) => void;
    onViewClick: (document: IViewDocumentModel) => void;
}

const DocumentRow = ({
    canDelete,
    document,
    documentsDownloading,
    documentsWithErrors,
    documentTypes,
    onDeleteClick,
    onDownloadClick,
    onViewClick,
}: IDocumentRowProps) => {
    const { buildZoneUrl } = useZoneContext();
    const [expanded, setExpanded] = useState<boolean>(false);
    const [childDocuments, setChildDocuments] = useState<IViewDocumentModel[]>([]);

    const {
        data: reportChildDocuments,
        isLoading,
        isSuccess,
        refetch: refetchChildDocuments,
    } = useGetChildDocuments(document.guid!, expanded);

    useEffect(() => {
        if (isSuccess) {
            setChildDocuments(reportChildDocuments ?? []);
        }
    }, [isSuccess, reportChildDocuments]);

    const handleToggle = async () => {
        setExpanded(!expanded);
    };

    const handleViewClick = async () => {
        onViewClick(document);
    };

    const getDocumentTypeEnumValue = (type: string): DocumentTypeEnum | undefined => {
        const map: { [key: string]: DocumentTypeEnum } = {
            WorkProduct: DocumentTypeEnum.WorkProduct,
            OfficialCaseDocument: DocumentTypeEnum.OfficialCaseDocument,
            Report: DocumentTypeEnum.Report,
            DataExport: DocumentTypeEnum.DataExport,
            ScreenPDF: DocumentTypeEnum.ScreenPDF,
        };
        return map[type];
    };

    //Todo:@@archana - As discussed - strategicplan and executionplan is saved (when we choose save - pdf) into database documents table only, but not in the entitydocuments table..
    const getCollabNavigationUrl = (document: IViewDocumentModel): string => {
        let navigationUrl = '';

        const documentTypeEnumValue = getDocumentTypeEnumValue(document.type);

        switch (documentTypeEnumValue) {
            case DocumentTypeEnum.Report:
                if (document.subType === 'Weekly Recap') {
                    navigationUrl = LocalRoutes.WeeklyRecapCollab.replace(
                        ':guid',
                        document.entityGuid!
                    );
                } else if (document.subType === 'Monthly Update') {
                    navigationUrl = LocalRoutes.MonthlyRecapCollab.replace(
                        ':guid',
                        document.entityGuid!
                    );
                } else if (document.subType === 'KPI Report') {
                    navigationUrl = LocalRoutes.KpiReportCollab.replace(
                        ':guid',
                        document.entityGuid!
                    );
                }
                break;
            case DocumentTypeEnum.ScreenPDF:
                //JurisdictionStrategyDetailExport, PlaintiffsFirmStrategyDetailExport, ProjectDetailExport, LocalCounselStrategyDetailExport, ExpertStrategyDetailExport
                switch (document.strategySubTypeId!) {
                    case StrategyTypes.Jurisdiction.Value:
                        navigationUrl = LocalRoutes.JurisdictionStrategyDetail.replace(
                            ':guid',
                            document.entityGuid!
                        );
                        break;

                    case StrategyTypes.PlaintiffsFirm.Value:
                        navigationUrl = LocalRoutes.PlaintiffFirmStrategyDetail.replace(
                            ':guid',
                            document.entityGuid!
                        );
                        break;

                    case StrategyTypes.LocalCounsel.Value:
                        navigationUrl = LocalRoutes.LocalCounselStrategyDetail.replace(
                            ':guid',
                            document.entityGuid!
                        );
                        break;

                    case StrategyTypes.Experts.Value:
                        navigationUrl = LocalRoutes.ExpertDetail.replace(
                            ':guid',
                            document.entityGuid!
                        );
                        break;

                    case StrategyTypes.Projects.Value:
                        navigationUrl = LocalRoutes.ProjectDetail.replace(
                            ':guid',
                            document.entityGuid!
                        );
                        break;

                    case StrategyTypes.StrategicPlan.Value: //todo:aj this is not required.
                        navigationUrl = LocalRoutes.StrategyExecutionPlan;
                        break;

                    default:
                        break;
                }

                break;
            default:
                break;
        }

        return buildZoneUrl(navigationUrl);
    };

    const handleDeleteDocument = (document: IViewDocumentModel, isChild: boolean) => {
        if (isChild) {
            onDeleteClick(document, refetchChildDocuments);
        } else {
            onDeleteClick(document);
        }
    };

    const downloadFailed = documentsWithErrors.includes(document.guid ?? '');

    return (
        <>
            <tr
                key={document.guid}
                style={{
                    verticalAlign: 'middle',
                    fontWeight: document.isRead ? 'normal' : 'bold',
                }}
            >
                {document.isCollab && document.hasChildren ? (
                    <td className="col-sm-3">
                        <div
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 8,
                                paddingRight: '5px',
                            }}
                        >
                            <ToggleIcon onClick={handleToggle} isOpen={expanded} />
                            <Link to={getCollabNavigationUrl(document)} target="_blank">
                                {document.filename}
                            </Link>
                        </div>
                    </td>
                ) : (
                    <td className="col-sm-3" style={{ paddingRight: 15 }}>
                        {document.isCollab ? (
                            <Link to={getCollabNavigationUrl(document)} target="_blank">
                                {document.filename}
                            </Link>
                        ) : (
                            <Link to="#" onClick={(e: any) => onDownloadClick(e, document)}>
                                {document.filename}
                            </Link>
                        )}
                    </td>
                )}
                <td className="col-sm-2">{document.type}</td>
                <td className="col-sm-2">{document.subType}</td>
                <td>{document.isCollab && <i className="fal fa-users" />}</td>
                <td className="col-sm-2">{Common.dateTimeFormatToLocal(document.createdDate)}</td>
                <td className="col-sm-2">{document.status}</td>
                <td className="text-center" style={{ paddingRight: 30 }}>
                    {document.isRead && <>Yes</>}
                </td>

                <td>
                    {documentTypes.find((lookup) => lookup.id === document.typeId!)?.displayName}
                </td>

                <td>
                    <div
                        className="text-nowrap"
                        style={{
                            display: 'flex',
                            gap: 5,
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                        }}
                    >
                        {document.isCollab ? (
                            <div style={{ width: 20, textAlign: 'center' }}>
                                <Link
                                    to={getCollabNavigationUrl(document)}
                                    target="_blank"
                                    onClick={handleViewClick}
                                >
                                    <i className="fal fa-lg fa-eye" style={{ width: 20 }} />
                                </Link>
                            </div>
                        ) : (
                            <div style={{ width: 20, textAlign: 'center' }}>
                                {documentsDownloading.includes(document.guid ?? '') ? (
                                    <i
                                        style={{ cursor: 'pointer' }}
                                        className="fal fa-spinner fa-solid fa-spin"
                                    />
                                ) : (
                                    <i
                                        style={{ cursor: 'pointer' }}
                                        className={classNames(
                                            'fal fa-lg fa-file-download',
                                            downloadFailed && 'text-danger'
                                        )}
                                        title={downloadFailed ? 'Download failed' : ''}
                                        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                                            onDownloadClick(e, document)
                                        }
                                    />
                                )}
                            </div>
                        )}

                        {canDelete ? (
                            <button
                                className="btn btn-no-bg px-2"
                                onClick={() => handleDeleteDocument(document!, false)}
                            >
                                <i className="fal fa-lg fa-trash-alt"></i>
                            </button>
                        ) : (
                            <div style={{ minHeight: '30px' }}></div>
                        )}
                    </div>
                </td>
            </tr>
            {expanded && document.isCollab && document.hasChildren && (
                <ExpandableRow isExpanded={expanded} colSpan={9}>
                    <div style={{ padding: '2em', background: '#fbfbfb' }}>
                        <div style={{ display: 'inline-block' }}>
                            <Stack
                                direction="horizontal"
                                gap={5}
                                style={{
                                    fontWeight: '600',
                                    opacity: '0.8',
                                    paddingLeft: '1em',
                                    marginBottom: '0.5em',
                                }}
                            >
                                <div style={{ width: 300 }}>Filename</div>
                                <div style={{ width: 115 }}>Date</div>
                                <div style={{ width: 50, textAlign: 'center' }}>Read</div>
                                <div style={{ width: 70 }}>Type</div>
                            </Stack>
                            {isLoading ? (
                                <>Loading child documents</>
                            ) : (
                                <>
                                    {childDocuments.map((childDocument) => (
                                        <ChildDocument
                                            canDelete={canDelete}
                                            childDocument={childDocument}
                                            isDownloading={documentsDownloading.includes(
                                                childDocument.guid ?? ''
                                            )}
                                            onDelete={handleDeleteDocument}
                                            onDownload={onDownloadClick}
                                            key={childDocument.guid}
                                        />
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                </ExpandableRow>
            )}
        </>
    );
};

export default DocumentRow;